import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import useClientRepresentativeQuery from '~/modules/projects/graphql/useClientRepDropdownForProject';
import {
  useAdditionalOptions,
  NO_VALUE_OPTION_ID
} from '~/modules/common/hooks';
import {
  CreatableDropdownItem,
  NoneDropdownItem,
  useOptionsMerge,
  useInputClasses
} from '~/modules/common/components/SearchAutocomplete';

const getOptionLabel = option => (option && option.displayText) || '';

const getOptionSelected = (option, selected) =>
  option.displayText === selected.displayText;

const useStyles = makeStyles(theme => ({
  option: {
    fontSize: theme.typography.body2.fontSize
  },
  noOptions: {
    color: theme.typography.body2.fontSize
  },
  loading: {
    color: theme.typography.body2.fontSize
  }
}));

const renderOption = option => {
  if (option.id === NO_VALUE_OPTION_ID)
    return <NoneDropdownItem value={option.label} />;

  return <CreatableDropdownItem option={option} optionPropText="displayText" />;
};

export const SearchableClientRepDropdown = ({
  clientUri,
  value,
  onChange,
  enableNoValueOption = false
}) => {
  const selectedClasses = useInputClasses({ value });
  const { formatMessage } = useIntl();
  const { loading, data } = useClientRepresentativeQuery(clientUri);
  const additionalOptions = useAdditionalOptions({
    enableNoValueOption,
    noValueMessageId: 'dropdownItem.noclientRepresentative'
  });
  const clientRepresentatives = (data && data.getClientRepresentatives) || [];

  const classes = useStyles({
    classes: selectedClasses
  });

  const renderInput = useCallback(
    params => {
      return (
        <TextField
          {...params}
          label={formatMessage({ id: 'clientRepresentative.label' })}
        />
      );
    },
    [formatMessage]
  );

  const onSubmit = useCallback((_, val) => onChange(val), [onChange]);

  const options = useOptionsMerge({
    options: clientRepresentatives,
    additionalOptions
  });

  return (
    <Autocomplete
      classes={classes}
      loading={loading}
      options={options}
      size="small"
      loadingText={formatMessage({ id: 'taskBeatTable.loading' })}
      noOptionsText={formatMessage({
        id: 'clientRepresentative.noclientRepresentative'
      })}
      fullWidth
      value={value || ''}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      onChange={onSubmit}
      renderInput={renderInput}
      renderOption={enableNoValueOption ? renderOption : null}
    />
  );
};

SearchableClientRepDropdown.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  clientUri: PropTypes.string,
  enableNoValueOption: PropTypes.bool
};

export default SearchableClientRepDropdown;
